/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {ChangeEvent, KeyboardEvent} from 'react';

import {gitHubClientAtom} from './jotai';
import useRefreshPullRequest from './useRefreshPullRequest';
import {KebabHorizontalIcon, PencilIcon, TrashIcon} from '@primer/octicons-react';
import {ActionList, ActionMenu, Box, Button, Flash, IconButton, Textarea} from '@primer/react';
import {useAtomValue} from 'jotai';
import {useCallback, useRef, useState} from 'react';

type CommentActionsMenuProps = {
  viewerCanUpdate: boolean;
  viewerCanDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

export function CommentActionsMenu({
  viewerCanUpdate,
  viewerCanDelete,
  onEdit,
  onDelete,
}: CommentActionsMenuProps): React.ReactElement | null {
  if (!viewerCanUpdate && !viewerCanDelete) {
    return null;
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label="Comment actions"
          size="small"
          sx={{color: 'fg.muted'}}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          {viewerCanUpdate && (
            <ActionList.Item onSelect={onEdit}>
              <ActionList.LeadingVisual>
                <PencilIcon />
              </ActionList.LeadingVisual>
              Edit
            </ActionList.Item>
          )}
          {viewerCanDelete && (
            <ActionList.Item variant="danger" onSelect={onDelete}>
              <ActionList.LeadingVisual>
                <TrashIcon />
              </ActionList.LeadingVisual>
              Delete
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

type EditCommentFormProps = {
  commentId: string;
  initialBody: string;
  onCancel: () => void;
  onSaved: () => void;
};

export function EditCommentForm({
  commentId,
  initialBody,
  onCancel,
  onSaved,
}: EditCommentFormProps): React.ReactElement {
  const [body, setBody] = useState(initialBody);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const client = useAtomValue(gitHubClientAtom);
  const refreshPullRequest = useRefreshPullRequest();

  const onSave = useCallback(async () => {
    if (client == null || body.trim() === '') {
      return;
    }
    setDisabled(true);
    setError(null);
    try {
      await client.updatePullRequestReviewComment({
        pullRequestReviewCommentId: commentId,
        body,
      });
      refreshPullRequest();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setDisabled(false);
    }
  }, [client, commentId, body, refreshPullRequest, onSaved]);

  const isDisabled = disabled || body.trim() === '';

  const isDisabledRef = useRef(isDisabled);
  isDisabledRef.current = isDisabled;

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (!isDisabledRef.current) {
          onSave();
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onSave, onCancel],
  );

  return (
    <Box sx={{mt: 1}}>
      {error != null && (
        <Flash variant="danger" sx={{mb: 2}}>
          {error}
        </Flash>
      )}
      <Textarea
        value={body}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setBody(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        autoFocus
        resize="vertical"
        sx={{height: '80px', mb: 1, width: '100%'}}
      />
      <Box display="flex" justifyContent="flex-end" sx={{gap: 1}}>
        <Button onClick={onCancel} disabled={disabled}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSave} disabled={isDisabled}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

type UseDeleteCommentResult = {
  deleteComment: () => void;
  isDeleting: boolean;
  deleteError: string | null;
};

export function useDeleteComment(commentId: string): UseDeleteCommentResult {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const client = useAtomValue(gitHubClientAtom);
  const refreshPullRequest = useRefreshPullRequest();

  const deleteComment = useCallback(async () => {
    if (client == null) {
      return;
    }
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await client.deletePullRequestReviewComment({id: commentId});
      refreshPullRequest();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
      setIsDeleting(false);
    }
  }, [client, commentId, refreshPullRequest]);

  return {deleteComment, isDeleting, deleteError};
}
